<template>
  <div class="eventActionList">
    <InputText
      v-if="showSearchField"
      v-model="search"
      class="w-100 mb-2"
      :placeholder="t('components.SearchList.inputSearchPlaceholder')"
      data-testid="event-list-search"
    />
    <div v-if="loading" class="flex justify-content-center my-5">
      <ProgressSpinner />
    </div>
    <DynamicScroller
      v-else
      ref="dynamicScroller"
      :items="idEvents"
      type-field="__typename"
      key-field="uid"
      :min-item-size="81"
      class="scroller"
    >
      <template #default="{ item, index, active }">
        <DynamicScrollerItem :item="item" :active="active" :size-dependencies="[item.description]">
          <EventRow
            :key="item.uid"
            :event="item.event"
            :class="{
              row2n: index % 2,
              selectable,
              selected: isSelected(item.event),
            }"
            :row2n="index % 2"
            :event-types="eventTypes"
            :action-types="actionTypes"
            :show-actions="showActions"
            :translation-size="
              (dynamicScroller?.$refs?.scroller?.sizes?.[index]?.accumulator ?? 0) -
              (dynamicScroller?.$refs?.scroller?.sizes?.[index]?.size ?? 0)
            "
            @click="toggleSelection(item.event)"
            @removed-from-board="removeEventFromLoadedBoard"
          />
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import ProgressSpinner from "primevue/progressspinner";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import type {
  EABActionTypeType,
  EABEventTypeType,
} from "@/modules/sqdc/components/eventActionBoard/EventActionHelper";
import InputText from "primevue/inputtext";
import { computed, inject, provide, ref, watch, type PropType } from "vue";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import EventRow from "./EventRow.vue";
import {
  BoardIdInjectionKey,
  EventActionButtonsDisabledKey,
  FilteredEventsInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import type { EventListEventFieldsFragment } from "@/graphql";
import Fuse from "fuse.js";
import { provideSQDCWorkflowInstancesBatch } from "@/modules/sqdc/workflow/useSQDCWorkflows";

const props = defineProps({
  loading: {
    type: Boolean,
    required: true,
  },
  eventTypes: {
    type: Array as PropType<EABEventTypeType[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<EABActionTypeType[]>,
    required: false,
    default: () => [],
  },
  events: {
    type: Array as PropType<EventListEventFieldsFragment[]>,
    default: undefined,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  showSearchField: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
});
const selectedEvents = defineModel<EventListEventFieldsFragment[]>("selectedEvents", {
  default: () => [],
});

provide(
  EventActionButtonsDisabledKey,
  computed(() => props.selectable),
);

const dynamicScroller = ref<null | InstanceType<typeof DynamicScroller>>(null);
const injectedFilteredEvents = inject(FilteredEventsInjectionKey, undefined);
const boardId = inject(BoardIdInjectionKey, undefined);
const search = ref("");
const removedEventIds = ref<ReadonlySet<number>>(new Set());

const sourceEvents = computed(
  () => props.events ?? injectedFilteredEvents?.value.activeEvents ?? [],
);
const workflowEvents = computed(() =>
  sourceEvents.value.flatMap((event) =>
    event.__typename === "SQDCEventOldFormatNewtuff" ? [event.newFormat] : [],
  ),
);
const workflowEventIds = computed(() => workflowEvents.value.map((event) => event.id));
const workflowActionIds = computed(() => [
  ...new Set(
    workflowEvents.value.flatMap((event) =>
      event.ActionEventConnection.flatMap(({ action }) => (action == null ? [] : [action.id])),
    ),
  ),
]);

provideSQDCWorkflowInstancesBatch(workflowEventIds, workflowActionIds);

const visibleEvents = computed(() => {
  const eventsOnLoadedBoard = sourceEvents.value.filter(
    (event) =>
      event.__typename !== "SQDCEventOldFormatNewtuff" || !removedEventIds.value.has(event.id),
  );
  if (search.value.length < 1) {
    return eventsOnLoadedBoard;
  }
  const fuse = new Fuse(eventsOnLoadedBoard, {
    isCaseSensitive: false,
    shouldSort: true,
    threshold: 0,
    ignoreLocation: true,
    minMatchCharLength: 2,
    keys: ["__typename", "id", "description", "owner.preferredName"],
  });
  return fuse.search(search.value).map(({ item }) => item);
});

function removeEventFromLoadedBoard(eventId: number) {
  removedEventIds.value = new Set([...removedEventIds.value, eventId]);
  selectedEvents.value = selectedEvents.value.filter(
    (event) => event.__typename !== "SQDCEventOldFormatNewtuff" || event.id !== eventId,
  );
}

if (boardId != null) {
  watch(boardId, () => {
    removedEventIds.value = new Set();
  });
}

function isSelected(event: EventListEventFieldsFragment) {
  return selectedEvents.value.some(
    (selected) => selected.__typename === event.__typename && selected.id === event.id,
  );
}

function toggleSelection(event: EventListEventFieldsFragment) {
  if (!props.selectable) {
    return;
  }
  selectedEvents.value = isSelected(event)
    ? selectedEvents.value.filter(
        (selected) => selected.__typename !== event.__typename || selected.id !== event.id,
      )
    : [...selectedEvents.value, event];
}

// The DynamicScroller needs a unique id, so this will be added to the event at this place.
const idEvents = computed(() => {
  return visibleEvents.value.map((event, index) => {
    return { event, uid: `${event.__typename}|${event.id}|${index}` };
  });
});

const { t } = useTranslation();
</script>
<style lang="scss" scoped>
.eventActionList {
  display: flex;
  flex-direction: column;
  margin-top: 0.2em;
  flex: 1;
  height: 0;
}

.scroller {
  flex: 1;
  height: 0;
  min-height: 30em;
  padding-bottom: 3.5em;
}

.selectable {
  position: relative;
  cursor: pointer;

  &::after {
    position: absolute;
    z-index: 1;
    top: 4px;
    bottom: 4px;
    left: 0;
    width: 4px;
    border-radius: 0 3px 3px 0;
    background-color: transparent;
    content: "";
    pointer-events: none;
    transition: background-color 120ms ease-in-out;
  }

  &:hover::after {
    background-color: var(--p-primary-200);
  }
}

.selected::after,
.selected:hover::after {
  background-color: var(--p-primary-color);
}
</style>

<style lang="scss">
.scroller {
  > .vue-recycle-scroller__item-wrapper {
    overflow: visible;
  }
}
</style>
